import React, { useState, useEffect } from 'react';
import { apiClient } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Bell, BellOff, Check, CheckCheck, PartyPopper, Zap, ChevronDown, ChevronUp, X, Trash2, Link, FileText, ExternalLink } from 'lucide-react';
import EnableNotificationsButton from '../components/EnableNotificationsButton';
import { clearAppBadge, setAppBadge } from '../lib/push';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Keep the home-screen icon badge in sync with whatever's in state, so
  // marking single notifications read / deleting them updates the dot
  // immediately on the current device (without waiting for the push roundtrip).
  useEffect(() => {
    const unread = notifications.filter((n) => !n.is_read).length;
    if (unread === 0) {
      clearAppBadge();
    } else {
      setAppBadge(unread);
    }
  }, [notifications]);

  const fetchNotifications = async () => {
    try {
      const res = await apiClient.get('/notifications');
      setNotifications(res.data);

      // Keep the home-screen icon badge in sync with the unread count
      const unread = res.data.filter((n) => !n.is_read).length;
      if (unread === 0) {
        clearAppBadge();
      } else {
        setAppBadge(unread);
      }

      // CRITICAL FIX: If no notifications exist, force the badge to sync
      // This handles cases where the unread count is out of sync with actual notifications
      if (res.data.length === 0 || res.data.every(n => n.is_read)) {
        // Trigger a refresh to ensure badge shows correct count
        window.dispatchEvent(new Event('notificationsUpdated'));
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await apiClient.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllRead = async () => {
    try {
      await apiClient.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
      // Notify Layout to update the unread count badge
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const clearAllNotifications = async () => {
    try {
      await apiClient.delete('/notifications/clear-all');
      setNotifications([]);
      toast.success('All notifications cleared');
      // Notify Layout to update the unread count badge
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (err) {
      toast.error('Failed to clear notifications');
    }
  };

  const deleteNotification = async (id) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setExpandedId(null);
      toast.success('Notification deleted');
      // Notify Layout to update the unread count badge
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (err) {
      toast.error('Failed to delete notification');
    }
  };

  const toggleExpand = (id) => {
    // Mark as read when expanding
    const notification = notifications.find(n => n.id === id);
    if (notification && !notification.is_read) {
      markAsRead(id);
    }
    setExpandedId(expandedId === id ? null : id);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'milestone':
        return <PartyPopper className="text-[#EFB342]" size={24} />;
      case 'automation':
        return <Zap className="text-purple-500" size={24} />;
      default:
        return <Bell className="text-stone-600" size={24} />;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Format message with line breaks
  const formatMessage = (message) => {
    if (!message) return '';
    return message.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i < message.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 animate-fade-in">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">NOTIFICATIONS</h1>
            <p className="text-stone-600 text-base">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
            </p>
          </div>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllRead}
                className="border-stone-300 text-stone-700 hover:bg-stone-100"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck size={16} className="mr-2" />
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllNotifications}
                className="border-stone-300 text-red-400 hover:bg-red-950/30 hover:border-red-700"
                data-testid="clear-all-notifications-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Home-screen notification toggle */}
        <div className="mb-5 bg-white/40 border border-stone-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="push-toggle-card">
          <div>
            <p className="text-sm font-semibold text-stone-900">Home-screen alerts</p>
            <p className="text-xs text-stone-600 mt-0.5">
              See a red dot on the RxGRIT icon when there&apos;s a new notification — even when the app is closed.
            </p>
          </div>
          <EnableNotificationsButton />
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {notifications.length > 0 ? (
            notifications.map((notification, i) => {
              const isExpanded = expandedId === notification.id;
              const isLongMessage = notification.message && notification.message.length > 100;
              
              return (
                <div
                  key={notification.id}
                  className={`bg-white/50 border rounded-2xl overflow-hidden transition-all duration-300 animate-fade-in ${
                    notification.is_read 
                      ? 'border-stone-200' 
                      : 'border-[#EFB342]/30 bg-[#EFB342]/5'
                  } ${isExpanded ? 'ring-1 ring-[#EFB342]/50' : ''}`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`notification-${notification.id}`}
                >
                  {/* Clickable Header */}
                  <button
                    onClick={() => toggleExpand(notification.id)}
                    className="w-full p-4 text-left hover:bg-stone-100/30 transition-colors"
                  >
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          notification.type === 'milestone' 
                            ? 'bg-[#EFB342]/20' 
                            : notification.type === 'automation'
                              ? 'bg-purple-500/20'
                              : 'bg-stone-100'
                        }`}>
                          {getIcon(notification.type)}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-stone-900 text-left text-base md:text-lg">{notification.title}</h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!notification.is_read && (
                              <span className="w-2 h-2 bg-[#EFB342] rounded-full"></span>
                            )}
                            {isLongMessage && (
                              isExpanded ? (
                                <ChevronUp size={18} className="text-stone-500" />
                              ) : (
                                <ChevronDown size={18} className="text-stone-500" />
                              )
                            )}
                          </div>
                        </div>
                        
                        {/* Preview or Full Message */}
                        <p className={`text-stone-600 text-base mt-1 text-left ${
                          !isExpanded && isLongMessage ? 'line-clamp-2' : ''
                        }`}>
                          {isExpanded ? formatMessage(notification.message) : notification.message}
                        </p>
                        
                        <p className="text-stone-400 text-sm mt-2 text-left">
                          {new Date(notification.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </button>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-stone-200">
                      {/* Image attachment for notifications with images */}
                      {notification.image_url && (
                        <div className="mt-4 rounded-xl overflow-hidden border border-stone-300">
                          <img 
                            src={notification.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${notification.image_url}` : notification.image_url} 
                            alt="Notification attachment"
                            className="w-full max-h-64 object-contain bg-stone-50"
                          />
                        </div>
                      )}
                      
                      {/* GIF for milestone notifications - object-contain so full image fits */}
                      {notification.gif_url && (
                        <div className="mt-4 rounded-xl overflow-hidden bg-stone-50 flex items-center justify-center">
                          <img 
                            src={notification.gif_url} 
                            alt="Celebration"
                            className="w-full max-h-48 object-contain"
                          />
                        </div>
                      )}
                      
                      {/* Links and attachments */}
                      {notification.links && notification.links.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-sm text-stone-500 uppercase tracking-wide">Attachments & Links</p>
                          {notification.links.map((link, idx) => (
                            <a
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-3 bg-stone-100/50 rounded-lg hover:bg-stone-100 transition-colors group"
                            >
                              {link.is_pdf ? (
                                <FileText className="text-red-400" size={18} />
                              ) : (
                                <Link className="text-[#EFB342]" size={18} />
                              )}
                              <span className="flex-1 text-stone-900 text-base">{link.label}</span>
                              <ExternalLink size={14} className="text-stone-500 group-hover:text-[#EFB342]" />
                            </a>
                          ))}
                        </div>
                      )}
                      
                      {/* Action buttons */}
                      <div className="flex items-center justify-end gap-2 mt-4">
                        {!notification.is_read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            className="text-stone-600 hover:text-stone-900 text-base"
                          >
                            <Check size={16} className="mr-1" />
                            Mark as read
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="text-stone-600 hover:text-red-400 text-base"
                          data-testid={`delete-notification-${notification.id}`}
                        >
                          <Trash2 size={16} className="mr-1" />
                          Delete
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedId(null)}
                          className="text-stone-600 hover:text-stone-900 text-base"
                        >
                          <X size={16} className="mr-1" />
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-stone-100/50 rounded-full mx-auto flex items-center justify-center mb-4">
                <BellOff className="text-stone-400" size={40} />
              </div>
              <h3 className="font-heading text-xl text-stone-900 mb-2">No Notifications</h3>
              <p className="text-stone-500 text-base">
                You&apos;re all caught up! Check back later for updates.
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Notifications;
